import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Copy, Undo2, Redo2, Loader2 } from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import type { ContentType } from "@/types/ab-tests";
import { getBulletIndex } from "@/utils/ab-test-utils";
import { VariantBFieldEditor } from "./VariantBFieldEditor";

interface VariantBPaneProps {
  selectedFields: ContentType[];
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  variantATitle: string;
  variantABullets: Record<string, string>;
  onTitleChange: (value: string) => void;
  onBulletChange: (field: string, value: string) => void;
  onCopy: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onReset: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isParsing?: boolean;
  isGenerating?: boolean;
  /** WEB-003 (FR-17 alert values): set by WEB-010. */
  aiStatus?: { state: "applying" | "applied"; text: string } | null;
  fieldSources?: Record<string, "ai" | "ai-edited" | "manual">;
  onFieldSourceChange?: (field: string, source: "ai" | "ai-edited" | "manual") => void;
  onFieldBlurWithChange: (field: ContentType, value: string) => void;
  onEditingFieldChange?: (field: string | null) => void;
  rationaleSlot?: React.ReactNode;
  rationaleButton?: React.ReactNode;
  children?: React.ReactNode;
}

export function VariantBPane({
  selectedFields,
  variantBTitle,
  variantBBullets,
  variantATitle,
  variantABullets,
  onTitleChange,
  onBulletChange,
  onCopy,
  onUndo,
  onRedo,
  onReset,
  canUndo,
  canRedo,
  isParsing = false,
  isGenerating = false,
  aiStatus = null,
  fieldSources = {},
  onFieldSourceChange,
  onFieldBlurWithChange,
  onEditingFieldChange,
  rationaleSlot,
  rationaleButton,
  children,
}: VariantBPaneProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [focusValue, setFocusValue] = useState("");
  // FR-17: one switch, off at mount, drives every tested field.
  const [viewChanges, setViewChanges] = useState(false);

  useEffect(() => {
    if (editingField !== null) {
      onEditingFieldChange?.(editingField);
    }
  }, [editingField, onEditingFieldChange]);

  return (
    <div className="flex-1 min-w-[420px] border-l border-border h-full flex flex-col">
      <div className="shrink-0 px-6 pt-6 pb-3 bg-card space-y-3 shadow-[0_4px_8px_-6px_rgba(0,0,0,0.08)]">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-semibold text-foreground">Variant B</h2>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" onClick={onCopy} className="h-7 w-7" aria-label="Copy Variant B content to clipboard">
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy Variant B content</TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={onUndo} disabled={!canUndo || isGenerating} className="h-7 px-2 text-xs" aria-label="Undo last change">
                  <Undo2 className="h-3.5 w-3.5" />
                  Undo
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={onRedo} disabled={!canRedo || isGenerating} className="h-7 px-2 text-xs" aria-label="Redo last change">
                  <Redo2 className="h-3.5 w-3.5" />
                  Redo
                </Button>
                {/* An in-flight AI apply overwrites the field text, strategy link and hypothesis when it lands --
                    Undo/Redo/Reset must not let the user act on a draft that is about to be replaced out from under them. */}
                <Button type="button" variant="ghost" size="sm" onClick={onReset} disabled={isGenerating} className="h-7 px-2 text-xs" aria-label="Reset Variant B to Variant A">
                  Reset
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Your in-progress draft. Defaults to Variant A — edit directly or use a builder below.
            </p>
          </div>

          {isGenerating ? (
            <Alert variant="ai" aria-live="polite" className="motion-safe:animate-soft-pulse">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <AlertTitle className="mb-0">
                {aiStatus?.state === "applying" ? aiStatus.text : "Signal AI is rewriting Variant B…"}
              </AlertTitle>
            </Alert>
          ) : aiStatus?.state === "applied" ? (
            <Alert variant="ai" aria-live="polite">
              <AISparkles className="h-4 w-4 shrink-0" />
              <AlertTitle className="mb-0">{aiStatus.text}</AlertTitle>
            </Alert>
          ) : null}
          {isParsing && !isGenerating && (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2" role="alert" aria-live="polite">
              <AISparkles className="h-4 w-4 shrink-0" />
              <span className="text-sm font-medium text-foreground">Signal AI is parsing the content</span>
            </div>
          )}

          <div className="space-y-4 max-h-[30vh] overflow-y-auto">
            {selectedFields.map((field, index) => {
              const isTitle = field === "title";
              const idx = getBulletIndex(field);
              const label = isTitle ? "TITLE" : `BULLET ${(idx ?? 0) + 1}`;
              const fieldValue = isTitle ? variantBTitle : (variantBBullets[field] ?? "");
              const aSource = isTitle ? variantATitle : (variantABullets[field] ?? "");
              const charLimit = isTitle ? 200 : 500;

              return (
                <VariantBFieldEditor
                  key={field}
                  field={field}
                  fieldValue={fieldValue}
                  aSource={aSource}
                  charLimit={charLimit}
                  label={label}
                  isEditing={editingField === field}
                  fieldSource={fieldSources[field]}
                  focusValue={focusValue}
                  viewChanges={viewChanges}
                  isFirstField={index === 0}
                  onViewChangesChange={setViewChanges}
                  onFocus={(val) => setFocusValue(val)}
                  onBlur={(f, val) => { setEditingField(null); onFieldBlurWithChange(f, val); }}
                  onChange={(val) => { if (isTitle) onTitleChange(val); else onBulletChange(field, val); }}
                  onClick={() => { if (editingField !== field) setEditingField(field); }}
                  onFieldSourceChange={onFieldSourceChange}
                />
              );
            })}
          </div>

          {rationaleButton && <div className="flex justify-end pt-1">{rationaleButton}</div>}
        </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
        {rationaleSlot}
        {children}
      </div>
    </div>
  );
}
